import { axiosInstance } from '../clients/axiosClient';
import { APPOINT_API_PREFIX, API_V1_PREFIX } from '../utils/constants';

import {
  AppointmentLoadStrategy,
  AppointmentFilter,
  ListMemberAppointmentsResponse,
  CreateAppointmentRequestPydantic,
  CreateAppointmentResponse,
  HTTPValidationError,
  UpdateAppointmentRequestPydantic,
  UpdateAppointmentResponse,
  ListAllAppointmentsResponse
} from '../types/apiGeneratedTypes';
import { AppointmentsTableActiveTab } from '../components/Members/Appointments';

export type GlobalAppointmentsApiFilters = {
  startDate?: string | null;
  endDate?: string | null;
  doctorId?: string | null;
  nutritionistId?: string | null;
};

export type GlobalAppointmentsFilters = {
  filterType: AppointmentsTableActiveTab;
  doctorId: string | null;
  nutritionistId: string | null;
  startDate: Date | null;
  endDate: Date | null;
};

export const getAppointmentsList = async (
  limit: number,
  offset: number,
  member_id: string,
  loading_strategy: AppointmentLoadStrategy | null = AppointmentLoadStrategy.ALL_DATA,
  filters?: AppointmentFilter | null,
  sort_direction?: null | 'asc' | 'desc',
  sort_field?: string | null,
): Promise<ListMemberAppointmentsResponse> => {
  try {
    const params = new URLSearchParams();

    params.append('loading_strategy', loading_strategy ?? AppointmentLoadStrategy.ALL_DATA);
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());
    if (member_id) params.append('member_id', member_id);
    if (filters) params.append('filters', JSON.stringify(filters));
    if (sort_direction) params.append('sort_direction', sort_direction);
    if (sort_field) params.append('sort_field', sort_field);

    const requestUrl = `${API_V1_PREFIX}${APPOINT_API_PREFIX}list?${params.toString()}`;

    const { data } = await axiosInstance.get(requestUrl);

    return data;
  } catch (error: any) {
    if (error.response && error.response.detail) {
      throw new Error(error.response.detail);
    }
    throw new Error(error);
  }
};

export const getAllAppointmentsList = async (
  limit: number,
  offset: number,
  type: string = 'all',
  filters?: GlobalAppointmentsApiFilters
): Promise<ListAllAppointmentsResponse> => {
  try {
    const params = new URLSearchParams();
    params.append('filter_type', type);
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());
    if (filters?.startDate) params.append('start_date', filters.startDate);
    if (filters?.endDate) params.append('end_date', filters.endDate);
    if (filters?.doctorId) params.append('doctor_id', filters.doctorId);
    if (filters?.nutritionistId) params.append('nutritionist_id', filters.nutritionistId);

    const requestUrl = `${API_V1_PREFIX}${APPOINT_API_PREFIX}all?${params.toString()}`;

    const { data } = await axiosInstance.get(requestUrl);
    return data;
  } catch (error: any) {
    if (error.response && error.response.detail) {
      throw new Error(error.response.detail);
    }
    throw new Error(error);
  }
};

export const createAppointment = async (
  newAppointmentRequestObject: CreateAppointmentRequestPydantic
): Promise<CreateAppointmentResponse | HTTPValidationError> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}${APPOINT_API_PREFIX}`,
      newAppointmentRequestObject
    );

    return data;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.data.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error);
  }
};

export const getAppointment = async (
  id: number,
  loading_strategy: AppointmentLoadStrategy | null = AppointmentLoadStrategy.ALL_DATA
): Promise<any> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${APPOINT_API_PREFIX}?appointment_id=${id}&loading_strategy=${loading_strategy}`
    );

    return data;
  } catch (error: any) {
    throw new Error(error);
  }
};

export const updateAppointment = async (
  appointmentRequestObject: UpdateAppointmentRequestPydantic
): Promise<UpdateAppointmentResponse | HTTPValidationError> => {
  try {
    const { data } = await axiosInstance.patch(
      `${API_V1_PREFIX}${APPOINT_API_PREFIX}`,
      appointmentRequestObject
    );

    return data;
  } catch (error: any) {
    throw new Error(error);
  }
};

export const deleteAppointment = async (
  appointment_id: number
): Promise<any> => {
  try {
    await axiosInstance.delete(
      `${API_V1_PREFIX}${APPOINT_API_PREFIX}${appointment_id}`
    );
    return true;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.data.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error);
  }
};
